import { ArrowDown, ShieldCheck, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";

const HERO_IMG =
    "https://images.unsplash.com/photo-1764885518098-781b23d50e7f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBob3NwaXRhbCUyMGJ1aWxkaW5nJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzc4NjYwNzcyfDA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
    const { t } = useTranslation();
    const stats = [
        { v: "500+", l: t("hero.stat_patients") },
        { v: "300+", l: t("hero.stat_doctors") },
        { v: "40+", l: t("hero.stat_countries") },
    ];
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative pt-36 md:pt-44 pb-16 md:pb-24 overflow-hidden"
        >
            {/* Decorative gradient blobs */}
            <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-brand-200/40 blur-3xl pointer-events-none" />
            <div className="absolute top-40 -left-24 h-80 w-80 rounded-full bg-amber-200/40 blur-3xl pointer-events-none" />

            <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                {/* Left content */}
                <div className="lg:col-span-7 animate-fade-up">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-xs font-medium tracking-wide">
                        <Sparkles className="h-3.5 w-3.5" />
                        {t("hero.trust")}
                    </div>

                    <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-stone-900 leading-[1.02]">
                        {t("hero.h1_p1")}
                        <span className="block text-brand-700 italic">
                            {t("hero.h1_india")}
                        </span>
                        <span className="block">{t("hero.h1_p2")}</span>
                    </h1>

                    <p className="mt-6 max-w-xl text-base md:text-lg text-stone-600 leading-relaxed">
                        {t("hero.desc")}
                    </p>

                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <Button
                            asChild
                            data-testid="hero-cta-enquiry"
                            className="rounded-full bg-orange-500 hover:bg-orange-600 text-white px-7 h-12 text-base shadow-lg hover:-translate-y-0.5 transition-all"
                        >
                            <a href="#enquiry">{t("hero.cta_primary")}</a>
                        </Button>
                        <Button
                            asChild
                            variant="outline"
                            data-testid="hero-cta-process"
                            className="rounded-full border-stone-300 hover:bg-stone-50 px-7 h-12 text-base"
                        >
                            <a href="#process" className="flex items-center gap-2">
                                {t("hero.cta_secondary")}
                                <ArrowDown className="h-4 w-4" />
                            </a>
                        </Button>
                    </div>

                    {/* Trust strip */}
                    <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
                        {stats.map((s, i) => (
                            <div
                                key={i}
                                className="border-l-2 border-brand-700 ps-3"
                                data-testid={`hero-stat-${i}`}
                            >
                                <div className="font-display text-2xl md:text-3xl font-bold text-stone-900">
                                    {s.v}
                                </div>
                                <div className="text-xs uppercase tracking-wider text-stone-500">
                                    {s.l}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right visual */}
                <div className="lg:col-span-5 relative">
                    <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-black/5">
                        <img
                            src={HERO_IMG}
                            alt="Modern hospital exterior"
                            className="absolute inset-0 h-full w-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/70 via-stone-900/10 to-transparent" />
                        <div className="absolute bottom-5 left-5 right-5 text-white">
                            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] mb-2">
                                <ShieldCheck className="h-4 w-4" />
                                {t("hero.badge_accred")}
                            </div>
                            <div className="font-display text-2xl font-semibold leading-snug">
                                {t("hero.badge_title")}
                            </div>
                        </div>
                    </div>

                    {/* Floating card */}
                    <div className="hidden md:block absolute -bottom-8 -left-8 bg-white rounded-2xl shadow-xl border border-stone-100 p-4 w-60">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                                <span className="text-amber-700 font-bold">★</span>
                            </div>
                            <div>
                                <div className="text-sm font-semibold text-stone-900">
                                    4.9 / 5
                                </div>
                                <div className="text-xs text-stone-500">
                                    {t("hero.rating_label")}
                                </div>
                            </div>
                        </div>
                        <p className="text-xs text-stone-600 mt-2 leading-snug">
                            “{t("hero.rating_quote")}”
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};
